// ARC Beacon — service abstraction layer.
//
// This used to be entirely mock data (see git history / services/mockData.js).
// It now calls the real Flask backend for the "core data" surface: auth,
// vehicles, drivers, trips, alerts, dashboard counters, team, facilities and
// geofences. Each exported function keeps the same name/signature/return
// shape the mock version had, so pages didn't need to change.
//    
// A few things the mock data invented (fleet map x/y coordinates, device
// signal strength, driver ratings, labeled trip waypoints, per-geofence
// entry/exit history…) have no real backend equivalent yet. Rather than
// fabricate numbers, those fields are set to a clearly-empty value (0, null,
// "—", or []) with a comment at the point they're produced below. Pages that
// are fundamentally tied to the illustrative fake map (Live Map, the
// Dashboard/Reports charts, geofence drawing) still read `mockData` directly
// and are unaffected by this file.
import apiClient from "@/lib/apiClient";
import { setToken, clearToken } from "@/lib/apiClient";
import { createCache } from "./entityCache";
import { initials } from "@/lib/format";
import * as mock from "./mockData";

const LATENCY = 320;
const delay = (data, ms = LATENCY) =>
  new Promise((resolve) => setTimeout(() => resolve(structuredCloneSafe(data)), ms));

function structuredCloneSafe(data) {
  if (Array.isArray(data)) return data.map((d) => ({ ...d }));
  if (data && typeof data === "object") return { ...data };
  return data;
}

// ---------------------------------------------------------------- caches
// See entityCache.js for why these exist. `vehicleDriverMap` is separate
// because the backend only exposes the vehicle<->driver link from the
// driver side (GET /api/drivers includes `assigned_vehicle`); vehicles
// don't carry a driver_id of their own.
const vehicleCache = createCache();
const driverCache = createCache();
const tripCache = createCache();
const alertCache = createCache();
const facilityCache = createCache();
const geofenceCache = createCache();
const vehicleDriverMap = new Map(); // vehicleId -> driverId

const ROLE_CODE_TO_LABEL = {
  org_admin: "Organization Admin",
  fleet_manager: "Fleet Manager",
  operator: "Operator",
  viewer: "Viewer",
};

function toDate(iso) {
  return iso ? new Date(iso) : null;
}

function latLngLabel(lat, lng) {
  return lat == null || lng == null ? null : `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
}

function isToday(value) {
  if (!value) return false;
  const d = value instanceof Date ? value : new Date(value);
  const now = new Date();
  return (
    d.getUTCFullYear() === now.getUTCFullYear() &&
    d.getUTCMonth() === now.getUTCMonth() &&
    d.getUTCDate() === now.getUTCDate()
  );
}

// Sums today's trips for a vehicle into the "cockpit" stats VehicleDetail
// renders. The backend has no ready-made per-vehicle "today" endpoint, so
// this is computed client-side from that vehicle's real trips.
function computeTodayAggregates(rawTrips) {
  const todays = (rawTrips || []).filter((t) => isToday(t.start_time));
  return {
    todayDistance: todays.reduce((s, t) => s + (t.distance_km || 0), 0),
    todayDriveTime: Math.round(todays.reduce((s, t) => s + (t.duration_seconds || 0), 0) / 60),
    fuelConsumed: todays.reduce((s, t) => s + (t.fuel_used_l || 0), 0),
    idleTime: Math.round(todays.reduce((s, t) => s + (t.idle_seconds || 0), 0) / 60),
    hardBraking: todays.reduce((s, t) => s + (t.hard_braking_count || 0), 0),
    hardAccel: todays.reduce((s, t) => s + (t.hard_accel_count || 0), 0),
  };
}

// ---------------------------------------------------------------- mappers

function mapVehicle(raw) {
  const name = raw.nickname || [raw.make, raw.model].filter(Boolean).join(" ") || raw.license_plate || "Vehicle";
  const device = raw.device
    ? {
        id: raw.device.bouncie_device_id || raw.device.id,
        uuid: raw.device.id,
        status: raw.device.status,
        signal: null, // not tracked by the backend yet
        firmware: null, // not tracked by the backend yet
        lastSeenAt: toDate(raw.device.last_seen_at),
      }
    : { id: "—", uuid: null, status: "disconnected", signal: null, firmware: null, lastSeenAt: null };

  return {
    id: raw.id,
    name,
    // Kept alongside `name` rather than folded into it: `name` falls back to
    // make/model/plate, so the edit form needs the real nickname to know
    // whether the field is set or merely being stood in for.
    nickname: raw.nickname || "",
    fleetTag: raw.license_plate || raw.nickname || "—",
    make: raw.make,
    model: raw.model,
    year: raw.year,
    vin: raw.vin || "",
    plate: raw.license_plate || "",
    status: raw.status,
    facilityId: raw.facility_id,
    driverId: vehicleDriverMap.get(raw.id) || null,
    location: {
      address: latLngLabel(raw.location?.latitude, raw.location?.longitude) || "Location unavailable",
      lat: raw.location?.latitude ?? null,
      lng: raw.location?.longitude ?? null,
    },
    speed: raw.speed ?? 0,
    heading: raw.location?.heading ?? 0,
    battery: raw.battery_pct,
    // "good" | "low" | "critical" | "unknown". Mirrors Bouncie's own
    // event-driven battery model (12.6V normal / 12V low / 11.5V critical);
    // "unknown" only for a vehicle whose device has never reported.
    batteryStatus: raw.battery_status || "unknown",
    fuel: raw.fuel_pct,
    odometer: raw.odometer_km,
    lastSeen: toDate(raw.last_seen_at),
    device,
    // Best-effort: counts whatever is currently warm in alertCache, so this
    // can under-count until alertService.list()/warmCoreCaches() resolves.
    alerts: alertCache.filter((a) => a.vehicleId === raw.id && a.status === "new").length,
    // "Today" driving aggregates require summing this vehicle's trips, which
    // list() doesn't fetch per-vehicle. vehicleService.get() overwrites these
    // with real computed values (see computeTodayAggregates above).
    todayDistance: 0,
    todayDriveTime: 0,
    fuelConsumed: 0,
    idleTime: 0,
    hardBraking: 0,
    hardAccel: 0,
  };
}

function mapDriver(raw) {
  return {
    id: raw.id,
    name: raw.full_name,
    initials: initials(raw.full_name),
    email: raw.email,
    phone: raw.phone,
    license: null, // not tracked by the backend
    rating: null, // not tracked by the backend
    status: raw.status,
    assignedVehicleId: raw.assigned_vehicle?.id || null,
    safetyScore: raw.safety_score,
    // Real per-driver "today" numbers require a per-driver trips lookup;
    // list() doesn't do that (would be one request per row). driverService
    // .get() fills these in for the single-driver detail page.
    tripsToday: 0,
    distanceToday: 0,
    safetyEvents: 0,
    lastActive: null, // not tracked by the backend
  };
}

function mapTripStatus(status) {
  return status === "live" ? "in-progress" : status;
}

function mapTrip(raw) {
  return {
    id: raw.id,
    vehicleId: raw.vehicle_id,
    driverId: raw.driver_id,
    status: mapTripStatus(raw.status),
    startTime: toDate(raw.start_time),
    endTime: toDate(raw.end_time),
    startLoc: raw.start?.address || latLngLabel(raw.start?.latitude, raw.start?.longitude) || "Unknown",
    endLoc:
      raw.status === "live"
        ? "In transit"
        : raw.end?.address || latLngLabel(raw.end?.latitude, raw.end?.longitude) || "Unknown",
    // Absolute odometer at each end, straight from Bouncie. null rather than 0
    // when absent — 0 would read as "the odometer said zero", and a trip that
    // arrived by webhook has no opening reading until the next REST sync.
    startOdometer: raw.start?.odometer ?? null,
    endOdometer: raw.end?.odometer ?? null,
    distanceKm: raw.distance_km ?? 0,
    durationMin: raw.duration_seconds != null ? Math.round(raw.duration_seconds / 60) : 0,
    fuelL: raw.fuel_used_l ?? 0,
    maxSpeed: raw.max_speed ?? 0,
    avgSpeed: raw.avg_speed ?? 0,
    hardBraking: raw.hard_braking_count ?? 0,
    hardAccel: raw.hard_accel_count ?? 0,
    idleSeconds: raw.idle_seconds ?? 0,
    // Fixed UTC offset in minutes (e.g. -300), not an IANA zone — see the
    // backend Trip model. null until this trip's first REST reconciliation;
    // format.js's clockTime/shortDate/dateSectionLabel fall back to the
    // browser's own zone when it's missing.
    utcOffsetMinutes: raw.utc_offset_minutes ?? null,
    // The illustrative map (route drawn as x/y%) and labeled waypoints
    // ("Entered Highway", …) were invented by the mock. The backend only has
    // raw GPS points (GET /api/trips/:id/route) with no labels — wiring a
    // real route/waypoint view needs a real map, which is out of scope here.
    route: null,
    waypoints: [],
  };
}

function mapAlert(raw) {
  return {
    id: raw.id,
    vehicleId: raw.vehicle_id,
    // The backend links alerts to a vehicle, not a driver. This falls back
    // to whoever is *currently* assigned to that vehicle, which may not be
    // who was driving at the moment the alert fired.
    driverId: vehicleDriverMap.get(raw.vehicle_id) || null,
    category: raw.category,
    type: raw.title || raw.type || "Alert",
    severity: raw.severity,
    status: raw.status,
    description: raw.description,
    geofenceName: raw.geofence_name || null,
    // The position recorded *with the alert*, which is where the event
    // happened. This used to read the vehicle's live location instead, so a
    // three-day-old geofence crossing was labelled with wherever the truck
    // happened to be sitting when the page loaded — and every alert for one
    // vehicle showed the identical coordinates. Null when the handler that
    // raised it recorded no position; callers show the time instead.
    location: latLngLabel(raw.latitude, raw.longitude),
    time: toDate(raw.occurred_at),
  };
}

function mapFacility(raw) {
  return {
    id: raw.id,
    name: raw.name,
    address: raw.address,
    latitude: raw.latitude,
    longitude: raw.longitude,
    vehicles: raw.counts?.vehicles ?? 0,
    activeVehicles: raw.counts?.active ?? 0,
    geofences: raw.counts?.geofences ?? 0,
    // No per-facility alert count from the backend yet; derived from
    // whatever is currently warm in the vehicle/alert caches.
    alerts: alertCache
      .all()
      .filter((a) => a.status !== "resolved" && vehicleCache.get(a.vehicleId)?.facilityId === raw.id).length,
  };
}

// Planar (equirectangular) approximation, projected around the polygon's
// own mean latitude — plenty accurate at the scale of a single geofence,
// and needs no map library loaded to compute.
function polygonAreaKm2(points) {
  if (!points || points.length < 3) return 0;
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const meanLat = points.reduce((s, [lat]) => s + lat, 0) / points.length;
  const cosLat = Math.cos(toRad(meanLat));
  const xy = points.map(([lat, lng]) => [toRad(lng) * R * cosLat, toRad(lat) * R]);
  let area = 0;
  for (let i = 0; i < xy.length; i++) {
    const [x1, y1] = xy[i];
    const [x2, y2] = xy[(i + 1) % xy.length];
    area += x1 * y2 - x2 * y1;
  }
  return Math.abs(area) / 2 / 1e6;
}

function mapGeofence(raw) {
  const facility = raw.facility_id ? facilityCache.get(raw.facility_id) : null;
  return {
    id: raw.id,
    name: raw.name,
    type: raw.shape_type,
    status: raw.is_active ? "active" : "disabled",
    facilityId: raw.facility_id,
    location: facility?.name || latLngLabel(raw.center?.latitude, raw.center?.longitude) || "—",
    radius: raw.radius_m,
    area: raw.shape_type === "polygon" ? Number(polygonAreaKm2(raw.polygon_coordinates).toFixed(2)) : 0,
    assignedVehicles: 0, // backend tracks live containment, not a distinct "assigned" set
    vehiclesInside: raw.vehicles_inside ?? 0,
    recentActivity: [], // no entry/exit event history endpoint exposed yet
    centerLat: raw.center?.latitude ?? null,
    centerLng: raw.center?.longitude ?? null,
    polygonCoordinates: raw.polygon_coordinates || null,
    alertOnEnter: raw.alert_on_enter ?? true,
    alertOnExit: raw.alert_on_exit ?? true,
    activeDays: raw.active_days ?? null, // null = every day; else ints 0=Sun..6=Sat
    activeStartTime: raw.active_start_time ?? null,
    activeEndTime: raw.active_end_time ?? null,
    // Whether this zone has been pushed to Bouncie, which decides how fast a
    // crossing is noticed: a synced zone reports within seconds by webhook,
    // an unsynced one is only caught by our own five-minute containment pass.
    // "pending" | "synced" | "error" | "disabled" | "unavailable"
    bouncieSyncState: raw.bouncie_sync_state || "pending",
    bouncieSyncedAt: toDate(raw.bouncie_synced_at),
    bouncieSyncError: raw.bouncie_sync_error || null,
  };
}

function mapTeamMember(raw) {
  return {
    id: raw.id,
    name: raw.full_name,
    initials: initials(raw.full_name),
    email: raw.email,
    role: ROLE_CODE_TO_LABEL[raw.role] || raw.role,
    status: raw.status,
    lastActive: toDate(raw.last_active_at),
  };
}

function mapAuthUser(raw) {
  return {
    id: raw.id,
    name: raw.full_name,
    initials: initials(raw.full_name),
    email: raw.email,
    role: ROLE_CODE_TO_LABEL[raw.role] || raw.role,
    status: raw.status,
    facilityId: null, // users aren't tied to a single facility in the backend model
    phone: null,
  };
}

// Fallback anchor point for geofenceService.create() when it's called
// without real drawn geometry (shouldn't happen via the UI — the draw-map
// tool always supplies real coordinates — kept as a defensive default
// rather than sending 0,0 to the backend).
function resolveDefaultCenter() {
  const withFacility = facilityCache.all().find((f) => f.latitude != null && f.longitude != null);
  if (withFacility) return { lat: withFacility.latitude, lng: withFacility.longitude };
  const withVehicle = vehicleCache.all().find((v) => v.location.lat != null);
  if (withVehicle) return { lat: withVehicle.location.lat, lng: withVehicle.location.lng };
  return null;
}

// ---------------------------------------------------------------- auth

export const authService = {
  async login({ email, password, two_factor_code } = {}) {
    let response;
    try {
      response = await apiClient.post("/api/auth/login", { email, password, two_factor_code });
    } catch (err) {
      const body = err.response?.data;
      if (body?.two_factor_required) {
        const twoFactorError = new Error(body.message || "Two-factor code required");
        twoFactorError.twoFactorRequired = true;
        throw twoFactorError;
      }
      throw new Error(body?.error || "Invalid email or password");
    }

    const { access_token, user } = response.data;
    setToken(access_token);
    const mappedUser = mapAuthUser(user);
    localStorage.setItem("arc_auth", JSON.stringify(mappedUser));

    // Warms vehicles/drivers/trips/alerts before Login.jsx navigates away,
    // so the first page the user lands on already has synchronous lookups
    // (vehicleService.byId, tripService.forVehicle, …) available.
    await warmCoreCaches();

    return mappedUser;
  },
  logout() {
    clearToken();
    localStorage.removeItem("arc_auth");
    [vehicleCache, driverCache, tripCache, alertCache, facilityCache, geofenceCache].forEach((c) => c.clear());
    vehicleDriverMap.clear();
  },
  current() {
    try {
      const raw = localStorage.getItem("arc_auth");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  },
  async me() {
    const { data } = await apiClient.get("/api/auth/me");
    const mappedUser = mapAuthUser(data);
    localStorage.setItem("arc_auth", JSON.stringify(mappedUser));
    return mappedUser;
  },
  // The backend has no password-reset endpoint yet, so these stay as
  // harmless client-side stubs — the Forgot/Reset Password screens still
  // work, they just don't persist anything server-side.
  // Always resolves, even for an address with no account — the backend
  // answers identically either way so the endpoint can't be used to find out
  // who has an account. Surfacing an error here would undo that.
  async requestReset(email) {
    const { data } = await apiClient.post("/api/auth/forgot-password", { email });
    return data;
  },
  // Throws on an invalid/expired/already-used token so the page can offer a
  // fresh link instead of pretending it worked.
  async resetPassword(token, password) {
    const { data } = await apiClient.post("/api/auth/reset-password", { token, password });
    return data;
  },
  async acceptInvite(token, password, fullName) {
    const response = await apiClient.post("/api/auth/accept-invite", {
      token, password, full_name: fullName || undefined,
    });
    const { access_token, user } = response.data;
    setToken(access_token);
    const mappedUser = mapAuthUser(user);
    localStorage.setItem("arc_auth", JSON.stringify(mappedUser));
    await warmCoreCaches();
    return mappedUser;
  },
  async changePassword(currentPassword, newPassword) {
    const { data } = await apiClient.post("/api/auth/change-password", {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return data;
  },
};

export const notificationPreferencesService = {
  async get() {
    const { data } = await apiClient.get("/api/auth/me");
    return data.notification_preferences;
  },
  async update(patch) {
    const { data } = await apiClient.patch("/api/auth/me/notification-preferences", patch);
    return data;
  },
};

export const twoFactorService = {
  async status() {
    const { data } = await apiClient.get("/api/auth/me");
    return data.two_factor; // { enabled, recovery_codes_remaining }
  },
  async enrol() {
    const { data } = await apiClient.post("/api/auth/2fa/enrol");
    return data; // { secret, provisioning_uri, digits, period_seconds, next_step }
  },
  async confirm(code) {
    const { data } = await apiClient.post("/api/auth/2fa/confirm", { code });
    return data; // { enabled, recovery_codes, message }
  },
  async disable(code, password) {
    const { data } = await apiClient.post("/api/auth/2fa/disable", { code, password });
    return data;
  },
};

export const apiKeyService = {
  async status() {
    const { data } = await apiClient.get("/api/auth/api-key");
    return data; // { has_key, display_hint }
  },
  async issue() {
    const { data } = await apiClient.post("/api/auth/api-key");
    return data; // { api_key, display_hint, replaced_existing, warning } — plaintext shown once
  },
  async revoke() {
    const { data } = await apiClient.delete("/api/auth/api-key");
    return data; // { revoked }
  },
};

// Fetches the core entity lists once so the synchronous accessors used
// throughout the app (byId/forVehicle) have something to read. Drivers are
// fetched before vehicles so vehicle->driver assignment resolves correctly
// on the very first render.
export async function warmCoreCaches() {
  try {
    await driverService.list();
  } catch {
    /* best-effort — pages will still work, just show "Unassigned" until retried */
  }
  try {
    await vehicleService.list();
  } catch {
    /* best-effort */
  }
  await Promise.allSettled([tripService.list(), alertService.list()]);
}

// ---------------------------------------------------------------- org / facilities

function mapOrganization(raw) {
  return {
    id: raw.id,
    name: raw.name,
    address: raw.address,
    timezone: raw.timezone,
    units: raw.units,
    hasApiKey: raw.has_api_key,
  };
}

export const orgService = {
  async get() {
    const { data } = await apiClient.get("/api/organization");
    return mapOrganization(data);
  },
  async update(patch) {
    const { data } = await apiClient.patch("/api/organization", patch);
    return mapOrganization(data);
  },
  async facilities() {
    const { data } = await apiClient.get("/api/facilities");
    const list = (data.facilities || []).map(mapFacility);
    facilityCache.warm(list);
    return list;
  },
  async facility(id) {
    if (facilityCache.get(id)) return facilityCache.get(id);
    const list = await orgService.facilities();
    return list.find((f) => f.id === id) || null;
  },
  async createFacility(input) {
    const body = {
      name: input.name,
      address: input.address || undefined,
      latitude: input.latitude !== "" && input.latitude != null ? Number(input.latitude) : undefined,
      longitude: input.longitude !== "" && input.longitude != null ? Number(input.longitude) : undefined,
    };
    const { data } = await apiClient.post("/api/facilities", body);
    // create_facility's response has no `counts` block (only the list
    // endpoint computes those) — mapFacility already defaults them to 0,
    // which is correct here: a brand-new facility has no vehicles yet.
    const facility = mapFacility(data);
    facilityCache.put(facility);
    return facility;
  },
  async deleteFacility(id) {
    await apiClient.delete(`/api/facilities/${id}`);
    // Vehicles/geofences homed here come back with facility_id: null on
    // their next fetch — simplest correct thing is just dropping the
    // whole warm cache rather than trying to patch every dependent entry.
    facilityCache.clear();
  },
};

// ---------------------------------------------------------------- fleet / dashboard

export const fleetService = {
  async kpis() {
    const { data } = await apiClient.get("/api/dashboard/overview");
    const c = data.counters || {};
    return {
      totalVehicles: c.vehicles ?? 0,
      active: c.active ?? 0,
      moving: c.moving ?? 0,
      parked: c.parked ?? 0,
      idle: c.idle ?? 0,
      alerts: c.alerts ?? 0,
      offline: c.offline ?? 0,
    };
  },
  // Backs the Dashboard's "Distance driven" chart. The backend only
  // returns days that actually had a trip (GROUP BY), so gaps are filled
  // in here with 0 rather than the chart silently compressing to fewer
  // than 7 points.
  async distanceSeries(vehicleId) {
    const { data } = await apiClient.get("/api/dashboard/distance-series", {
      params: vehicleId ? { vehicle_id: vehicleId } : undefined,
    });
    const byDate = new Map((data.series || []).map((s) => [s.date, s.distance_km]));
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const iso = d.toISOString().slice(0, 10);
      days.push({ day: d.toLocaleDateString("en-US", { weekday: "short" }), km: byDate.get(iso) ?? 0 });
    }
    return days;
  },
};

// ---------------------------------------------------------------- reports

export const reportService = {
  async catalog() {
    const { data } = await apiClient.get("/api/reports");
    return data; // { reports: [{key, name, period_based}], periods, export_formats }
  },
  async get(key, period = "7d") {
    const { data } = await apiClient.get(`/api/reports/${key}`, { params: { period } });
    return data; // { report, period, data }
  },
  // Triggers a real file download — fetched as a blob (so the auth header
  // can be attached) rather than a plain <a href>, which couldn't carry it.
  async export(key, format, period = "7d") {
    const response = await apiClient.get(`/api/reports/${key}/export`, {
      params: { format, period },
      responseType: "blob",
    });
    const disposition = response.headers["content-disposition"] || "";
    const match = disposition.match(/filename="?([^"]+)"?/);
    const filename = match ? match[1] : `${key}.${format}`;

    const url = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
  // Opens the PDF in a new tab instead of force-downloading it — the
  // browser's own viewer/print dialog already offers "Save as PDF", so a
  // silent download isn't the only way to get a PDF out of this button.
  async printPdf(key, period = "7d") {
    const response = await apiClient.get(`/api/reports/${key}/export`, {
      params: { format: "pdf", period },
      responseType: "blob",
    });
    const url = URL.createObjectURL(response.data);
    const win = window.open(url, "_blank");
    if (win) {
      // Best-effort: ask the new tab to open its print dialog once the PDF
      // has rendered. Harmless if the browser ignores/blocks it — the tab
      // still shows the PDF with its own print control either way.
      win.addEventListener("load", () => {
        try { win.print(); } catch { /* ignored — viewer still usable */ }
      });
    }
    // The blob has to stay alive while the new tab may still be loading it;
    // revoking immediately (like export() does) can race that load.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },
};

// ---------------------------------------------------------------- vehicles

export const vehicleService = {
  async list(filters = {}) {
    const params = { limit: 500 };
    if (filters.status && filters.status !== "all") params.status = filters.status;
    if (filters.facilityId && filters.facilityId !== "all") params.facility_id = filters.facilityId;

    const { data } = await apiClient.get("/api/vehicles", { params });
    let list = (data.vehicles || []).map(mapVehicle);

    // The backend has no free-text search param; filter client-side same as
    // the old mock did.
    if (filters.q) {
      const q = filters.q.toLowerCase();
      list = list.filter((v) =>
        [v.name, v.vin, v.plate, v.make, v.model, v.device.id].join(" ").toLowerCase().includes(q)
      );
    }

    vehicleCache.warm(list);
    return list;
  },
  // Street address for a vehicle's current position, resolved server-side.
  //
  // Separate from get() on purpose: the backend bills a Google request for an
  // address it has not seen before, so it is fetched for the one vehicle on
  // screen rather than folded into every list row. Returns null when the
  // vehicle has no fix yet or the lookup did not resolve — the caller falls
  // back to coordinates.
  async address(id) {
    const { data } = await apiClient.get(`/api/vehicles/${id}/address`);
    return data.address ?? null;
  },
  async decodeVin(vin) {
    const { data } = await apiClient.get(`/api/vehicles/decode-vin/${encodeURIComponent(vin)}`);
    return data; // { make, model, year, vehicle_type, body_class, fuel_type, warning }
  },
  async get(id) {
    const [vehicleRes, tripsRes] = await Promise.all([
      apiClient.get(`/api/vehicles/${id}`),
      apiClient.get("/api/trips", { params: { vehicle_id: id, limit: 200 } }).catch(() => ({ data: { trips: [] } })),
    ]);

    const rawTrips = tripsRes.data.trips || [];
    tripCache.warm(rawTrips.map(mapTrip));

    const vehicle = { ...mapVehicle(vehicleRes.data), ...computeTodayAggregates(rawTrips) };
    vehicleCache.put(vehicle);
    return vehicle;
  },
  byId(id) {
    return vehicleCache.get(id);
  },
  all() {
    return vehicleCache.all();
  },
  async create(input) {
    const body = {
      make: input.make,
      model: input.model,
      year: input.year ? Number(input.year) : undefined,
      vin: input.vin,
      license_plate: input.licensePlate,
      nickname: input.nickname,
      facility_id: input.facilityId || undefined,
      device_id: input.deviceId || undefined,
    };
    const { data } = await apiClient.post("/api/vehicles", body);
    const vehicle = mapVehicle(data);
    vehicleCache.put(vehicle);
    return vehicle;
  },
  async update(id, patch) {
    const body = {};
    const fieldMap = {
      make: "make", model: "model", year: "year", vin: "vin",
      licensePlate: "license_plate", nickname: "nickname", facilityId: "facility_id",
    };
    for (const [from, to] of Object.entries(fieldMap)) {
      if (from in patch) body[to] = patch[from];
    }
    // The backend sets these straight onto the model with no type coercion —
    // sending "2023" (string) into an Integer column fails at the DB layer.
    if (body.year !== undefined) body.year = body.year === "" ? null : Number(body.year);
    const { data } = await apiClient.patch(`/api/vehicles/${id}`, body);
    const vehicle = { ...vehicleCache.get(id), ...mapVehicle(data) };
    vehicleCache.put(vehicle);
    return vehicle;
  },
  async remove(id) {
    await apiClient.delete(`/api/vehicles/${id}`);
    vehicleCache.clear(); // simplest correct thing — next list() call re-fetches
    return { ok: true };
  },
};

// ---------------------------------------------------------------- drivers

export const driverService = {
  async list() {
    const { data } = await apiClient.get("/api/drivers");
    const raw = data.drivers || [];

    vehicleDriverMap.clear();
    raw.forEach((d) => {
      if (d.assigned_vehicle?.id) vehicleDriverMap.set(d.assigned_vehicle.id, d.id);
    });

    const list = raw.map(mapDriver);
    driverCache.warm(list);

    // On a hard refresh landing directly on a page that reads driver ->
    // assigned-vehicle (e.g. Drivers.jsx calling vehicleService.byId), the
    // vehicle cache may still be cold at this point. Warm it in the
    // background so a follow-up render (or a manual refetch) resolves names
    // correctly instead of showing "Unassigned" indefinitely.
    if (vehicleCache.all().length === 0) {
      vehicleService.list().catch(() => {});
    }

    return list;
  },
  // No GET /api/drivers/:id endpoint exists — reuse the (tiny) list fetch.
  async get(id) {
    const list = await driverService.list();
    const driver = list.find((d) => d.id === id) || null;
    if (!driver) return null;

    if (driver.assignedVehicleId) {
      try {
        const { data } = await apiClient.get("/api/trips", {
          params: { vehicle_id: driver.assignedVehicleId, limit: 200 },
        });
        const rawTrips = data.trips || [];
        tripCache.warm(rawTrips.map(mapTrip));
        const todays = rawTrips.filter((t) => isToday(t.start_time));
        driver.tripsToday = todays.length;
        driver.distanceToday = todays.reduce((s, t) => s + (t.distance_km || 0), 0);
        driver.safetyEvents = alertCache.filter(
          (a) => a.vehicleId === driver.assignedVehicleId && a.category === "driving" && isToday(a.time)
        ).length;
      } catch {
        /* leave the zeroed defaults from mapDriver */
      }
    }

    // Real breakdown of *why* the score is what it is — the mock never had
    // this concept at all (rating/safetyScore there were just static numbers).
    try {
      driver.safetyBreakdown = await driverService.safetyScore(id);
    } catch {
      driver.safetyBreakdown = null;
    }

    driverCache.put(driver);
    return driver;
  },
  byId(id) {
    return driverCache.get(id);
  },
  async create(input) {
    const body = {
      full_name: input.fullName,
      email: input.email || undefined,
      phone: input.phone || undefined,
      status: input.status || undefined,
    };
    const { data } = await apiClient.post("/api/drivers", body);
    const driver = mapDriver(data);
    driverCache.put(driver);
    return driver;
  },
  async update(id, patch) {
    const body = {};
    const fieldMap = { fullName: "full_name", email: "email", phone: "phone", status: "status" };
    for (const [from, to] of Object.entries(fieldMap)) {
      if (from in patch) body[to] = patch[from];
    }
    const { data } = await apiClient.patch(`/api/drivers/${id}`, body);
    const driver = { ...driverCache.get(id), ...mapDriver(data) };
    driverCache.put(driver);
    return driver;
  },
  async assign(driverId, vehicleId) {
    const { data } = await apiClient.post(`/api/drivers/${driverId}/assign`, { vehicle_id: vehicleId });
    vehicleDriverMap.set(vehicleId, driverId);
    return data;
  },
  async unassign(driverId) {
    const driver = driverCache.get(driverId);
    const { data } = await apiClient.post(`/api/drivers/${driverId}/unassign`);
    if (driver?.assignedVehicleId) vehicleDriverMap.delete(driver.assignedVehicleId);
    return data;
  },
  async safetyScore(driverId, periodDays = 30) {
    const { data } = await apiClient.get(`/api/drivers/${driverId}/safety-score`, {
      params: { period_days: periodDays },
    });
    return data;
  },
  async recalculateScores(periodDays = 30) {
    const { data } = await apiClient.post("/api/drivers/recalculate-scores", { period_days: periodDays });
    return data;
  },
  async remove(id) {
    await apiClient.delete(`/api/drivers/${id}`);
    driverCache.clear(); // simplest correct thing — next list() call re-fetches
    return { ok: true };
  },
};

// ---------------------------------------------------------------- trips

export const tripService = {
  async list(filters = {}) {
    const params = { limit: 200 };
    if (filters.vehicleId && filters.vehicleId !== "all") params.vehicle_id = filters.vehicleId;

    const { data } = await apiClient.get("/api/trips", { params });
    let list = (data.trips || []).map(mapTrip);

    // driver_id/status aren't backend list filters — filter client-side.
    if (filters.driverId && filters.driverId !== "all") list = list.filter((t) => t.driverId === filters.driverId);
    if (filters.status && filters.status !== "all") list = list.filter((t) => t.status === filters.status);

    tripCache.warm(list);
    return list;
  },
  async get(id) {
    const { data } = await apiClient.get(`/api/trips/${id}`);
    const trip = mapTrip(data);
    tripCache.put(trip);
    return trip;
  },
  forVehicle(vehicleId) {
    return tripCache.filter((t) => t.vehicleId === vehicleId);
  },
  async route(id) {
    const { data } = await apiClient.get(`/api/trips/${id}/route`);
    // recordedAt was being dropped. The trip timeline positions everything by
    // elapsed time rather than by point index, because the sampling interval
    // is not constant — the median gap is 5s but gaps of nearly two minutes
    // occur, and an index-based x axis would stretch those flat.
    return (data.points || []).map((p) => ({
      lat: p.latitude,
      lng: p.longitude,
      speed: p.speed ?? null,
      heading: p.heading ?? null,
      recordedAt: toDate(p.recorded_at),
    }));
  },
  // Timestamped events inside a trip, plus the counts that have no timestamps.
  //
  // The split is the backend's and it matters: `events` are alerts with a real
  // occurred_at, so each carries a position from 0 to 1 along the trip and can
  // be drawn where it happened. `counts` are Bouncie's per-trip hard-braking
  // and rapid-acceleration totals — its tripMetrics payload gives a number for
  // the whole trip and no timestamps, so there is nowhere honest to put a dot.
  async events(id) {
    const { data } = await apiClient.get(`/api/trips/${id}/events`);
    return {
      durationSeconds: data.duration_seconds ?? null,
      events: (data.events || []).map((e) => ({
        id: e.id,
        code: e.code,
        category: e.category,
        severity: e.severity,
        title: e.title,
        occurredAt: toDate(e.occurred_at),
        offsetSeconds: e.offset_seconds,
        position: e.position,
      })),
      counts: {
        hardBraking: data.counts?.hard_braking ?? 0,
        hardAccel: data.counts?.hard_accel ?? 0,
      },
    };
  },
  // Trips older than the live window (see backend/app/services/archival_service.py
  // — a nightly job moves anything past ~20 days into trip_archive) live in a
  // separate table. `before`/`windowDays` walk backward one date window at a
  // time: pass the oldest startTime already loaded as the next `before`.
  async archive({ vehicleId, before, windowDays = 20 } = {}) {
    const params = { window_days: windowDays };
    if (before) params.before = before;
    if (vehicleId && vehicleId !== "all") params.vehicle_id = vehicleId;

    const { data } = await apiClient.get("/api/trips/archive", { params });
    return {
      trips: (data.trips || []).map(mapTrip),
      windowStart: data.window_start,
      hasMore: !!data.has_more,
    };
  },
};

// ---------------------------------------------------------------- alerts

export const alertService = {
  async list(filters = {}) {
    const params = { limit: 200 };
    if (filters.status && filters.status !== "all") params.status = filters.status;
    if (filters.category && filters.category !== "all") params.category = filters.category;

    const { data } = await apiClient.get("/api/alerts", { params });
    const list = (data.alerts || []).map(mapAlert);
    alertCache.warm(list);
    return list;
  },
  async acknowledge(id) {
    const { data } = await apiClient.post(`/api/alerts/${id}/acknowledge`);
    alertCache.put(mapAlert(data));
    return { ok: true };
  },
  async resolve(id) {
    const { data } = await apiClient.post(`/api/alerts/${id}/resolve`);
    alertCache.put(mapAlert(data));
    return { ok: true };
  },
  forVehicle(vehicleId) {
    return alertCache.filter((a) => a.vehicleId === vehicleId);
  },
  // Same date-windowed pagination as tripService.archive, against alert_archive.
  async archive({ category, before, windowDays = 20 } = {}) {
    const params = { window_days: windowDays };
    if (before) params.before = before;
    if (category && category !== "all") params.category = category;

    const { data } = await apiClient.get("/api/alerts/archive", { params });
    return {
      alerts: (data.alerts || []).map(mapAlert),
      windowStart: data.window_start,
      hasMore: !!data.has_more,
    };
  },
};

// ---------------------------------------------------------------- geofences

export const geofenceService = {
  async list() {
    const { data } = await apiClient.get("/api/geofences");
    const list = (data.geofences || []).map(mapGeofence);
    geofenceCache.warm(list);
    return list;
  },
  async create(gf) {
    const shapeType = gf.type === "polygon" ? "polygon" : "circle";
    const body = {
      name: gf.name || "New Geofence", shape_type: shapeType, is_active: true,
      alert_on_enter: gf.alertOnEnter ?? true,
      alert_on_exit: gf.alertOnExit ?? true,
      active_days: gf.activeDays ?? null,
      active_start_time: gf.activeStartTime || null,
      active_end_time: gf.activeEndTime || null,
    };

    // The Geofences page's draw-on-map tool is the normal way a shape gets
    // here now, so real drawn geometry is expected. The old
    // resolveDefaultCenter() placeholder-box fallback only fires if this
    // ever gets called without it (defensive, shouldn't happen in the UI).
    if (shapeType === "circle") {
      const center = gf.centerLat != null && gf.centerLng != null
        ? { lat: gf.centerLat, lng: gf.centerLng }
        : resolveDefaultCenter();
      body.center_lat = center?.lat ?? 0;
      body.center_lng = center?.lng ?? 0;
      body.radius_m = gf.radiusM || gf.radius || 400;
    } else if (gf.coordinates?.length >= 3) {
      body.polygon_coordinates = gf.coordinates;
    } else {
      const center = resolveDefaultCenter();
      const lat = center?.lat ?? 0;
      const lng = center?.lng ?? 0;
      const d = 0.003; // ~300m
      body.polygon_coordinates = [
        [lat - d, lng - d],
        [lat - d, lng + d],
        [lat + d, lng + d],
        [lat + d, lng - d],
      ];
    }

    const { data } = await apiClient.post("/api/geofences", body);
    const mapped = mapGeofence(data);
    geofenceCache.put(mapped);
    return { id: mapped.id };
  },
  async update(id, patch) {
    const body = {};
    if ("status" in patch) body.is_active = patch.status === "active";
    if ("name" in patch) body.name = patch.name;
    if ("radius" in patch) body.radius_m = Number(patch.radius);
    if ("centerLat" in patch) body.center_lat = Number(patch.centerLat);
    if ("centerLng" in patch) body.center_lng = Number(patch.centerLng);
    if ("alertOnEnter" in patch) body.alert_on_enter = !!patch.alertOnEnter;
    if ("alertOnExit" in patch) body.alert_on_exit = !!patch.alertOnExit;
    if ("activeDays" in patch) body.active_days = patch.activeDays;
    if ("activeStartTime" in patch) body.active_start_time = patch.activeStartTime || null;
    if ("activeEndTime" in patch) body.active_end_time = patch.activeEndTime || null;
    const { data } = await apiClient.patch(`/api/geofences/${id}`, body);
    geofenceCache.put(mapGeofence(data));
    return { ok: true };
  },
  async remove(id) {
    await apiClient.delete(`/api/geofences/${id}`);
    geofenceCache.clear(); // ids don't get reused; simplest correct thing is a fresh list() next fetch
    return { ok: true };
  },
  // Retry the Bouncie push for one zone. Returns 202 with the geofence in its
  // new "pending" state — the push itself happens in a worker, so the caller
  // refetches rather than expecting a final state back.
  async syncToBouncie(id) {
    const { data } = await apiClient.post(`/api/geofences/${id}/sync`);
    const mapped = mapGeofence(data);
    geofenceCache.put(mapped);
    return mapped;
  },
};

// ---------------------------------------------------------------- team

const ROLE_LABEL_TO_CODE = Object.fromEntries(
  Object.entries(ROLE_CODE_TO_LABEL).map(([code, label]) => [label, code])
);

export const safetySettingsService = {
  async get() {
    const { data } = await apiClient.get("/api/settings/safety");
    return data;
  },
  async update(patch) {
    const { data } = await apiClient.put("/api/settings/safety", patch);
    return data;
  },
};

export const maintenanceService = {
  async list() {
    const { data } = await apiClient.get("/api/maintenance");
    return data.schedules || [];
  },
  async create({ vehicleId, serviceType, intervalKm, intervalDays }) {
    const body = {
      vehicle_id: vehicleId,
      service_type: serviceType,
      interval_km: intervalKm ? Number(intervalKm) : undefined,
      interval_days: intervalDays ? Number(intervalDays) : undefined,
    };
    const { data } = await apiClient.post("/api/maintenance", body);
    return data;
  },
  async logService(scheduleId) {
    const { data } = await apiClient.post(`/api/maintenance/${scheduleId}/log-service`);
    return data;
  },
  async due() {
    const { data } = await apiClient.get("/api/maintenance/due");
    return data.items || [];
  },
};

export const documentService = {
  async create({ vehicleId, docType, expiryDate }) {
    const body = { vehicle_id: vehicleId, doc_type: docType, expiry_date: expiryDate };
    const { data } = await apiClient.post("/api/documents", body);
    return data;
  },
  async expiring(warnDays = 30) {
    const { data } = await apiClient.get("/api/documents/expiring", { params: { warn_days: warnDays } });
    return data.items || [];
  },
};

export const teamService = {
  async list() {
    const { data } = await apiClient.get("/api/team");
    return (data.members || []).map(mapTeamMember);
  },
  // Team.jsx doesn't actually call this (it reads mockData.roleDefs
  // directly) — kept for API compatibility.
  roles: () => delay(mock.roleDefs),
  async invite({ email, role, fullName }) {
    const body = { email, role: ROLE_LABEL_TO_CODE[role] || role, full_name: fullName || undefined };
    const { data } = await apiClient.post("/api/team/invite", body);
    // The backend sends the invite email itself (falls back to a console
    // log if SMTP isn't configured) and reports whether it actually went
    // out; the link is still returned too, as a manual-share fallback.
    return { ...mapTeamMember(data), inviteUrl: data.invite_url, inviteEmailSent: !!data.invite_email_sent };
  },
  async changeRole(userId, roleLabel) {
    const body = { role: ROLE_LABEL_TO_CODE[roleLabel] || roleLabel };
    const { data } = await apiClient.patch(`/api/team/${userId}/role`, body);
    return mapTeamMember(data);
  },
  async setStatus(userId, status) {
    const { data } = await apiClient.patch(`/api/team/${userId}/status`, { status });
    return mapTeamMember(data);
  },
  async remove(userId) {
    await apiClient.delete(`/api/team/${userId}`);
  },
};

// ---------------------------------------------------------------- everything below is unchanged / still mocked
// Notifications, integrations and global search aren't part of this pass —
// see the CORS/API wiring summary for what's real vs. mock right now.

export const integrationService = {
  list: () => delay(mock.integrations),
};

export const bouncieService = {
  async status() {
    const { data } = await apiClient.get("/api/integrations/bouncie/status");
    // { connected, needs_reauth, expires_at, last_success_at, last_failure_at,
    //   last_error }. `connected` and `needs_reauth` are separate on purpose:
    // a token row can exist while the grant behind it is dead, and reporting
    // that as "connected" is what let the app disagree with Bouncie for hours
    // without saying so.
    return data;
  },
  async connect() {
    const { data } = await apiClient.get("/api/integrations/bouncie/connect");
    window.location.href = data.authorization_url; // full-page redirect to Bouncie — see backend comment for why
  },
  async disconnect() {
    const { data } = await apiClient.delete("/api/integrations/bouncie");
    return data;
  },
  async syncNow() {
    const { data } = await apiClient.post("/api/integrations/bouncie/sync");
    return data;
  },
};

const ICON_BY_CATEGORY = { health: "health", device: "device", geofence: "geofence", driving: "driving" };

function mapNotification(raw) {
  return {
    id: raw.id,
    icon: ICON_BY_CATEGORY[raw.category] || "battery", // fallback icon bucket for non-alert notifications
    title: raw.category ? `${raw.category[0].toUpperCase()}${raw.category.slice(1)} alert` : "Notification",
    vehicle: raw.vehicle,
    description: raw.message,
    time: toDate(raw.created_at),
    severity: raw.severity || "info",
    group: isToday(raw.created_at) ? "today" : "earlier",
    read: !!raw.read_at,
  };
}

export const notificationService = {
  // `before` (ISO cursor) walks further back a page at a time — pass the
  // oldest `time` already loaded to fetch the next older page.
  async list({ before, limit = 20 } = {}) {
    const params = { limit };
    if (before) params.before = before;
    const { data } = await apiClient.get("/api/notifications", { params });
    return {
      notifications: (data.notifications || []).map(mapNotification),
      hasMore: !!data.has_more,
      unreadCount: data.unread_count || 0,
    };
  },
  async unreadCount() {
    const { data } = await apiClient.get("/api/notifications", { params: { limit: 1 } });
    return data.unread_count || 0;
  },
  async markRead(id) {
    await apiClient.post(`/api/notifications/${id}/read`);
    return { ok: true };
  },
  async markAllRead() {
    const { data } = await apiClient.post("/api/notifications/mark-all-read");
    return data;
  },
  // Registers this browser's FCM device token against the logged-in user so
  // the backend can push to it. Safe to call repeatedly — the backend
  // upserts on (user, token).
  async registerFcmToken(token) {
    // Explicit even though the backend defaults to "web" — the Android/iOS
    // apps (separate codebases, same backend) send their own platform value
    // through this same endpoint, so this is the value that distinguishes
    // this app's registrations from theirs.
    await apiClient.post("/api/notifications/fcm-token", { token, platform: "web" });
    return { ok: true };
  },
  async unregisterFcmToken(token) {
    await apiClient.delete("/api/notifications/fcm-token", { data: { token } });
    return { ok: true };
  },
};

export const searchService = {
  async search(q) {
    const query = (q || "").trim();
    if (query.length < 2) return { vehicles: [], drivers: [], trips: [], facilities: [], geofences: [] };
    const { data } = await apiClient.get("/api/search", { params: { q: query, limit: 6 } });
    // Real results are a uniform {type, id, label, sublabel, url} per item,
    // grouped by type — no "alerts" type exists server-side, unlike the
    // old mock, since alerts aren't searchable by the backend today.
    const results = data.results || {};
    return {
      vehicles: results.vehicles || [],
      drivers: results.drivers || [],
      trips: results.trips || [],
      facilities: results.facilities || [],
      geofences: results.geofences || [],
    };
  },
};

// ---------------------------------------------------------------- parking

function parkingError(err, fallback) {
  return new Error(err.response?.data?.error || fallback);
}

function mapParkingSlot(raw) {
  return {
    id: raw.id,
    areaId: raw.area_id ?? null,
    number: raw.slot_number,
    row: raw.row_index,
    col: raw.col_index,
    polygon: raw.polygon_coordinates || [],
    center: raw.center ? { lat: raw.center.latitude, lng: raw.center.longitude } : null,
    occupied: !!raw.occupied,
    occupiedSince: toDate(raw.occupied_since),
    vehicle: raw.vehicle
      ? {
          id: raw.vehicle.id,
          name: raw.vehicle.nickname
            || [raw.vehicle.year, raw.vehicle.make, raw.vehicle.model].filter(Boolean).join(" ")
            || raw.vehicle.license_plate
            || raw.vehicle.vin
            || "Vehicle",
          plate: raw.vehicle.license_plate,
          vin: raw.vehicle.vin,
          status: raw.vehicle.status,
        }
      : null,
  };
}

function mapParkingArea(raw) {
  return {
    id: raw.id,
    name: raw.name,
    corners: raw.corners || [],
    cols: raw.cols,
    rowHeightM: raw.row_height_m,
    roadGapM: raw.road_gap_m,
    rowsPerBlock: raw.rows_per_block ?? 1,
    slotCount: raw.slot_count ?? 0,
  };
}

function mapParkingLayout(raw) {
  return {
    id: raw.id,
    facilityId: raw.facility_id,
    name: raw.name,
    isActive: raw.is_active,
    alertOnEnter: raw.alert_on_enter,
    alertOnExit: raw.alert_on_exit,
    totalSlots: raw.total_slots ?? 0,
    occupiedSlots: raw.occupied_slots ?? 0,
    vacantSlots: raw.vacant_slots ?? 0,
    areas: raw.areas ? raw.areas.map(mapParkingArea) : undefined,
    slots: raw.slots ? raw.slots.map(mapParkingSlot) : undefined,
  };
}

// One area as the API wants it. `slots` is the (possibly hand-edited) preview:
// when present the server stores exactly those outlines instead of regenerating.
function areaBody({ name, corners, cols, rowHeightM, roadGapM, rowsPerBlock, slots }) {
  const body = {
    corners, cols, row_height_m: rowHeightM, road_gap_m: roadGapM, rows_per_block: rowsPerBlock,
  };
  if (name) body.name = name;
  if (slots) {
    body.slots = slots.map((s) => ({
      row_index: s.row, col_index: s.col, polygon_coordinates: s.polygon,
    }));
  }
  return body;
}

export const parkingService = {
  async layouts(facilityId) {
    const { data } = await apiClient.get("/api/parking/layouts", {
      params: facilityId ? { facility_id: facilityId } : undefined,
    });
    return (data.layouts || []).map(mapParkingLayout);
  },
  // The board: a layout with its areas and every slot and who is in it.
  async layout(id) {
    const { data } = await apiClient.get(`/api/parking/layouts/${id}`);
    return mapParkingLayout(data);
  },
  // Generates slots from drawn corners without saving. With `layoutId` the
  // server also reports which existing areas the new corners overlap.
  async preview({ corners, cols, rowHeightM, roadGapM, rowsPerBlock, layoutId, excludeAreaId }) {
    try {
      const { data } = await apiClient.post("/api/parking/preview", {
        ...areaBody({ corners, cols, rowHeightM, roadGapM, rowsPerBlock }),
        layout_id: layoutId || undefined,
        exclude_area_id: excludeAreaId || undefined,
      });
      return {
        rows: data.rows,
        cols: data.cols,
        slotCount: data.slot_count,
        widthM: data.width_m,
        heightM: data.height_m,
        slotWidthM: data.slot_width_m,
        overlaps: data.overlaps || [],
        slots: data.slots.map(mapParkingSlot),
      };
    } catch (err) {
      throw parkingError(err, "Could not generate a preview");
    }
  },
  // A new lot, optionally with its first area (same shape as addArea's input).
  async create({ facilityId, name, area, alertOnEnter, alertOnExit }) {
    try {
      const { data } = await apiClient.post("/api/parking/layouts", {
        facility_id: facilityId, name,
        area: area ? areaBody(area) : undefined,
        alert_on_enter: alertOnEnter, alert_on_exit: alertOnExit,
      });
      return mapParkingLayout(data);
    } catch (err) {
      throw parkingError(err, "Could not save the parking layout");
    }
  },
  async update(id, patch) {
    const body = {};
    if ("name" in patch) body.name = patch.name;
    if ("isActive" in patch) body.is_active = patch.isActive;
    if ("alertOnEnter" in patch) body.alert_on_enter = patch.alertOnEnter;
    if ("alertOnExit" in patch) body.alert_on_exit = patch.alertOnExit;
    try {
      const { data } = await apiClient.patch(`/api/parking/layouts/${id}`, body);
      return mapParkingLayout(data);
    } catch (err) {
      throw parkingError(err, "Could not update the parking layout");
    }
  },
  async remove(id) {
    try {
      await apiClient.delete(`/api/parking/layouts/${id}`);
    } catch (err) {
      throw parkingError(err, "Could not delete the parking layout");
    }
  },
  // Scan another area into a lot; saved immediately.
  async addArea(layoutId, area) {
    try {
      const { data } = await apiClient.post(`/api/parking/layouts/${layoutId}/areas`, areaBody(area));
      return { area: mapParkingArea(data.area), overlaps: data.overlaps || [] };
    } catch (err) {
      throw parkingError(err, "Could not save the area");
    }
  },
  // Re-scan an existing area (new corners / settings / hand-edited slots).
  // Slots still in the grid keep their ids and whoever is parked in them.
  async updateArea(areaId, area) {
    try {
      const { data } = await apiClient.put(`/api/parking/areas/${areaId}`, areaBody(area));
      return { area: mapParkingArea(data.area), overlaps: data.overlaps || [] };
    } catch (err) {
      throw parkingError(err, "Could not update the area");
    }
  },
  // Move / rotate a whole area rigidly. rotateDeg is anticlockwise about the
  // area's centre, applied before the east/north shift (metres).
  async transformArea(areaId, { eastM = 0, northM = 0, rotateDeg = 0 }) {
    try {
      const { data } = await apiClient.post(`/api/parking/areas/${areaId}/transform`, {
        east_m: eastM, north_m: northM, rotate_deg: rotateDeg,
      });
      return { area: mapParkingArea(data.area), overlaps: data.overlaps || [] };
    } catch (err) {
      throw parkingError(err, "Could not move the area");
    }
  },
  // Copy an area with its slots; by default the copy lands just south of it.
  async duplicateArea(areaId) {
    try {
      const { data } = await apiClient.post(`/api/parking/areas/${areaId}/duplicate`, {});
      return { area: mapParkingArea(data.area), overlaps: data.overlaps || [] };
    } catch (err) {
      throw parkingError(err, "Could not duplicate the area");
    }
  },
  async renameArea(areaId, name) {
    try {
      await apiClient.patch(`/api/parking/areas/${areaId}`, { name });
    } catch (err) {
      throw parkingError(err, "Could not rename the area");
    }
  },
  async removeArea(areaId) {
    try {
      await apiClient.delete(`/api/parking/areas/${areaId}`);
    } catch (err) {
      throw parkingError(err, "Could not delete the area");
    }
  },
  // Manual corrections to saved slots, applied together or not at all.
  // `updates`: [{ id, polygon }], `deletes`: [slotId],
  // `creates`: [{ areaId, polygon, row, col }] (duplicated / new slots).
  async editSlots(layoutId, { updates = [], deletes = [], creates = [] }) {
    try {
      await apiClient.post(`/api/parking/layouts/${layoutId}/slots/edit`, {
        updates: updates.map((u) => ({ id: u.id, polygon_coordinates: u.polygon })),
        deletes,
        creates: creates.map((c) => ({
          area_id: c.areaId, polygon_coordinates: c.polygon, row_index: c.row ?? 0, col_index: c.col ?? 0,
        })),
      });
    } catch (err) {
      throw parkingError(err, "Could not save the slot changes");
    }
  },
  async releaseSlot(slotId) {
    try {
      await apiClient.post(`/api/parking/slots/${slotId}/release`);
    } catch (err) {
      throw parkingError(err, "Could not release the slot");
    }
  },
};
